import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatPrice, apiErr, imageUrl } from "@/lib/api";
import { COLOMBIA_CITIES } from "@/lib/cities";
import { toast } from "sonner";

export default function CheckoutPage() {
    const { items, totalPrice, clearCart } = useCart();
    const { user } = useAuth();
    const nav = useNavigate();
    const [form, setForm] = useState({
        customer_name: user?.name || "", customer_email: user?.email || "", customer_phone: "",
        address: "", city: "", notes: "", payment_method: "whatsapp",
    });
    const [loading, setLoading] = useState(false);
    const { data: settings } = useQuery({ queryKey: ["settings-co"], queryFn: async () => (await api.get("/settings")).data });
    const cityNorm = form.city.trim().toLowerCase();
    const cityRate = cityNorm ? (settings?.shipping_rates || []).find((r) => (r.city || "").trim().toLowerCase() === cityNorm) : null;
    const shipping = cityRate ? (Number(cityRate.cost) || 0) : (settings?.shipping_cost || 0);
    const grand = totalPrice + shipping;

    const submit = async (e) => {
        e.preventDefault();
        if (items.length === 0) { toast.error("Tu carrito está vacío"); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/checkout", {
                ...form,
                items: items.map((i) => ({ productId: i.productId, name: i.name, price: i.price, size: i.size, color: i.color, quantity: i.quantity, image: i.image })),
            });
            if (form.payment_method === "whatsapp" && settings?.whatsapp) {
                const lines = items.map((i) => `• ${i.name} (Talla ${i.size} · ${i.color}) x${i.quantity} — ${formatPrice(i.price * i.quantity)}`).join("\n");
                const msg = `Hola! Quiero confirmar mi pedido en J&M American Shop:\n\n${lines}\n\nEnvío: ${formatPrice(shipping)}\nTOTAL: ${formatPrice(grand)}\n\nNombre: ${form.customer_name}\nCiudad: ${form.city}\nDirección: ${form.address}\nN° de pedido: ${data.order_id}`;
                window.open(`https://wa.me/${String(settings.whatsapp).replace(/\D/g, "")}?text=${encodeURIComponent(msg)}`, "_blank");
            }
            clearCart();
            if (data.checkout_url) window.location.assign(data.checkout_url);
            else nav(`/pedido/${data.order_id}`);
        } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
    };

    if (items.length === 0) {
        return <div className="py-24 text-center text-sm text-ink/60">Tu carrito está vacío.</div>;
    }

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const inp = "w-full border border-sandLight bg-white rounded-md px-4 py-3 text-sm outline-none focus:border-ink";

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-8 py-10">
            <h1 className="font-display text-4xl text-ink mb-8">Checkout</h1>
            <form onSubmit={submit} className="grid lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2 space-y-8">
                    <section>
                        <p className="text-eyebrow mb-4">Datos de contacto</p>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <input required placeholder="Nombre completo" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className={inp} data-testid="checkout-name" />
                            <input required type="email" placeholder="Email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} className={inp} data-testid="checkout-email" />
                            <input required placeholder="Teléfono" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} className={inp} />
                            <select required value={form.city} onChange={(e) => set("city", e.target.value)} className={inp} data-testid="checkout-city">
                                <option value="" disabled>Seleccioná tu ciudad</option>
                                {COLOMBIA_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                            <p className="text-xs text-ink/50 -mt-2">{cityRate ? `Envío a ${form.city}: ${formatPrice(shipping)}` : form.city.trim() ? `Envío nacional: ${formatPrice(shipping)}` : "El envío se calcula según tu ciudad"}</p>
                            <input required placeholder="Dirección completa" value={form.address} onChange={(e) => set("address", e.target.value)} className={`${inp} sm:col-span-2`} />
                            <textarea placeholder="Notas del pedido (opcional)" value={form.notes} onChange={(e) => set("notes", e.target.value)} className={`${inp} sm:col-span-2`} rows={3} />
                        </div>
                    </section>
                    <section>
                        <p className="text-eyebrow mb-4">Método de pago</p>
                        {[
                            { id: "whatsapp", label: "Continuar compra por WhatsApp", desc: "Tu pedido llega a nuestro WhatsApp con todo el detalle y coordinamos pago y entrega por chat" },
                        ].map((m) => (
                            <label key={m.id} data-testid={`pm-${m.id}`} className={`block border rounded-md p-4 cursor-pointer mb-3 ${form.payment_method === m.id ? "border-ink bg-white" : "border-sandLight"}`}>
                                <input type="radio" name="pm" checked={form.payment_method === m.id} onChange={() => set("payment_method", m.id)} className="mr-2 accent-ink" />
                                <span className="font-medium">{m.label}</span>
                                <span className="text-xs text-ink/60 block ml-6">{m.desc}</span>
                                {m.id === "whatsapp" && <span className="text-[11px] text-terracotta block ml-6 mt-1">Al confirmar se abre WhatsApp con el detalle de tu pedido listo para enviar.</span>}
                            </label>
                        ))}
                    </section>
                </div>
                <div className="bg-white border border-sandLight rounded-lg p-6 h-fit sticky top-24">
                    <p className="text-eyebrow mb-4">Resumen</p>
                    <div className="space-y-2 mb-4">
                        {items.map((i) => (
                            <div key={i.lineId} className="flex justify-between text-xs">
                                <span className="text-ink/70">{i.name} × {i.quantity}</span>
                                <span>{formatPrice(i.price * i.quantity)}</span>
                            </div>
                        ))}
                    </div>
                    <div className="border-t border-sandLight pt-3 space-y-1 text-sm">
                        <div className="flex justify-between"><span>Subtotal</span><span>{formatPrice(totalPrice)}</span></div>
                        <div className="flex justify-between"><span>Envío</span><span>{formatPrice(shipping)}</span></div>
                    </div>
                    <div className="border-t border-sandLight pt-3 mt-3 flex justify-between text-base font-medium"><span>TOTAL</span><span>{formatPrice(grand)}</span></div>
                    <button type="submit" data-testid="btn-mercadopago-pay" disabled={loading} className="btn-ink w-full mt-6 inline-flex items-center justify-center">{loading ? "Procesando…" : "Confirmar y enviar por WhatsApp"}</button>
                    <p className="text-[10px] text-ink/50 mt-3 text-center">Al confirmar se abre WhatsApp con tu pedido listo para enviar.</p>
                </div>
            </form>
        </div>
    );
}
